import React, { useMemo, useState } from "react";
import {
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import ShareIcon from "@mui/icons-material/Share";
import CloseIcon from "@mui/icons-material/Close";
import { Group, SharePermission } from "../../lib/types";
import SharePermissionToggle from "./SharePermissionToggle";

interface ShareRecordModalProps {
  open: boolean;
  onClose: () => void;
  groups: Group[];
  onShare: (groupIds: string[], permission: SharePermission) => void;
}

const ShareRecordModal: React.FC<ShareRecordModalProps> = ({
  open,
  onClose,
  groups,
  onShare,
}) => {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [permission, setPermission] = useState<SharePermission>("view");

  const toggleGroup = (groupId: string) => {
    setSelectedIds((prev) =>
      prev.includes(groupId)
        ? prev.filter((id) => id !== groupId)
        : [...prev, groupId]
    );
  };

  const canShare = useMemo(() => selectedIds.length > 0, [selectedIds]);

  const handleShare = () => {
    if (!canShare) {
      return;
    }
    console.log("Sharing record to groups", {
      groupIds: selectedIds,
      permission,
    });
    onShare(selectedIds, permission);
    setSelectedIds([]);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Share record</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography color="text.secondary">
            Choose the groups that should see this record.
          </Typography>
          <Stack spacing={1}>
            {groups.map((group) => (
              <FormControlLabel
                key={group.id}
                control={
                  <Checkbox
                    checked={selectedIds.includes(group.id)}
                    onChange={() => toggleGroup(group.id)}
                  />
                }
                label={group.name}
              />
            ))}
          </Stack>
          <SharePermissionToggle value={permission} onChange={setPermission} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Tooltip title="Cancel">
          <IconButton
            onClick={() => {
              console.log("Closing share modal");
              onClose();
            }}
          >
            <CloseIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Share">
          <span>
            <IconButton onClick={handleShare} disabled={!canShare}>
              <ShareIcon />
            </IconButton>
          </span>
        </Tooltip>
      </DialogActions>
    </Dialog>
  );
};

export default ShareRecordModal;
